import React, { useState } from "react";
import { toast } from "sonner";
import { patchTracerSettings, type TracerSettingsPatch } from "@client/api/tracer";
import { t } from "@client/lib/dictionary";
import { showWriteResults } from "@client/lib/verifyToast";
import { useUserStore } from "@client/store/user";
import type { TracerBatteryType, TracerSettings } from "@client/types/tracer";
import { Button } from "@client/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@client/components/ui/card";
import { Input } from "@client/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@client/components/ui/select";

interface Props {
  slug: string;
  settings: TracerSettings | null | undefined;
}

const errMsg = (err: unknown): string =>
  String((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? err);

/** Popisky polí pro ověřovací toasty (mapuje field klíč z WriteFieldResult). */
const FIELD_LABELS: Record<string, string> = {
  batteryType: t.tracer.settings.batteryType,
  batteryCapacityAh: t.tracer.settings.batteryCapacity,
  tempCompCoef: t.tracer.settings.tempCompCoef,
  highVoltageDisconnect: t.tracer.settings.highVoltageDisconnect,
  chargingLimitVoltage: t.tracer.settings.chargingLimitVoltage,
  overVoltageReconnect: t.tracer.settings.overVoltageReconnect,
  equalizationVoltage: t.tracer.settings.equalizationVoltage,
  boostVoltage: t.tracer.settings.boostVoltage,
  floatVoltage: t.tracer.settings.floatVoltage,
  boostReconnectVoltage: t.tracer.settings.boostReconnectVoltage,
  lowVoltageReconnect: t.tracer.settings.lowVoltageReconnect,
  underVoltageRecover: t.tracer.settings.underVoltageRecover,
  underVoltageWarning: t.tracer.settings.underVoltageWarning,
  lowVoltageDisconnect: t.tracer.settings.lowVoltageDisconnect,
  dischargingLimitVoltage: t.tracer.settings.dischargingLimitVoltage,
  eqChargingCycleDays: t.tracer.settings.eqChargingCycleDays,
  batTempWarnHigh: t.tracer.settings.batTempWarnHigh,
  batTempWarnLow: t.tracer.settings.batTempWarnLow,
};

const NumberField: React.FC<{
  label: string;
  current: number;
  dirty: number | undefined;
  step?: number;
  onChange: (v: number | undefined) => void;
}> = ({ label, current, dirty, step = 0.1, onChange }) => {
  const value = dirty !== undefined ? dirty : current;
  return (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      <Input
        type="number"
        step={step}
        value={Number.isFinite(value) ? value : ""}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "") {
            onChange(undefined);
          } else {
            onChange(Number(v));
          }
        }}
        className={dirty !== undefined ? "ring-2 ring-amber-500" : ""}
      />
    </div>
  );
};

export const TracerSettingsForm: React.FC<Props> = ({ slug, settings }) => {
  const user = useUserStore((s) => s.user);
  const isAdmin = typeof user === "object" && user !== null && (user as { role: string }).role === "ADMIN";

  const [dirty, setDirty] = useState<TracerSettingsPatch>({});
  const [busy, setBusy] = useState(false);

  // Reset "dirty" diff když se ze serveru přijme nová verze settings.
  // Pattern "store previous prop in state" doporučený v React docs místo useEffect+setState:
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevSettings, setPrevSettings] = useState(settings);
  if (settings !== prevSettings) {
    setPrevSettings(settings);
    setDirty({});
  }

  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.tracer.detail.settings}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">{t.tracer.controls.adminRequired}</CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.tracer.detail.settings}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">Čekám na settings z nabíječe…</CardContent>
      </Card>
    );
  }

  const setField = <K extends keyof TracerSettingsPatch>(key: K, value: TracerSettingsPatch[K]) => {
    setDirty((prev) => {
      if (value === undefined) {
        const { [key]: _omit, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: value };
    });
  };

  const handleSave = async () => {
    if (Object.keys(dirty).length === 0) {
      toast.info("Žádné změny");
      return;
    }
    setBusy(true);
    try {
      const res = await patchTracerSettings(slug, dirty);
      showWriteResults(res.responseObject?.results ?? [], FIELD_LABELS);
      // dirty se vyčistí; reálné hodnoty dorazí přes `settings` WS event (žádné dosazení).
      setDirty({});
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const numberPair = <K extends keyof TracerSettingsPatch>(label: string, field: K, current: number, step = 0.1) => (
    <NumberField
      key={String(field)}
      label={label}
      current={current}
      dirty={dirty[field] as number | undefined}
      step={step}
      onChange={(v) => setField(field, v as TracerSettingsPatch[K])}
    />
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.tracer.detail.settings}</CardTitle>
        <p className="text-xs text-amber-600">⚠ {t.tracer.settings.warning}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-xs text-muted-foreground">{t.tracer.settings.batteryType}</label>
          <Select
            value={(dirty.batteryType ?? settings.batteryType) as string}
            onValueChange={(v) => setField("batteryType", v as TracerBatteryType)}
          >
            <SelectTrigger className={dirty.batteryType !== undefined ? "ring-2 ring-amber-500" : ""}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USER">{t.tracer.batteryType.USER}</SelectItem>
              <SelectItem value="SEALED">{t.tracer.batteryType.SEALED}</SelectItem>
              <SelectItem value="GEL">{t.tracer.batteryType.GEL}</SelectItem>
              <SelectItem value="FLOODED">{t.tracer.batteryType.FLOODED}</SelectItem>
              <SelectItem value="LITHIUM">{t.tracer.batteryType.LITHIUM}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {numberPair(t.tracer.settings.batteryCapacity, "batteryCapacityAh", settings.batteryCapacityAh, 1)}
          {numberPair(t.tracer.settings.tempCompCoef, "tempCompCoef", settings.tempCompCoef, 0.1)}
          {numberPair(t.tracer.settings.highVoltageDisconnect, "highVoltageDisconnect", settings.highVoltageDisconnect)}
          {numberPair(t.tracer.settings.chargingLimitVoltage, "chargingLimitVoltage", settings.chargingLimitVoltage)}
          {numberPair(t.tracer.settings.overVoltageReconnect, "overVoltageReconnect", settings.overVoltageReconnect)}
          {numberPair(t.tracer.settings.equalizationVoltage, "equalizationVoltage", settings.equalizationVoltage)}
          {numberPair(t.tracer.settings.boostVoltage, "boostVoltage", settings.boostVoltage)}
          {numberPair(t.tracer.settings.floatVoltage, "floatVoltage", settings.floatVoltage)}
          {numberPair(t.tracer.settings.boostReconnectVoltage, "boostReconnectVoltage", settings.boostReconnectVoltage)}
          {numberPair(t.tracer.settings.lowVoltageReconnect, "lowVoltageReconnect", settings.lowVoltageReconnect)}
          {numberPair(t.tracer.settings.underVoltageRecover, "underVoltageRecover", settings.underVoltageRecover)}
          {numberPair(t.tracer.settings.underVoltageWarning, "underVoltageWarning", settings.underVoltageWarning)}
          {numberPair(t.tracer.settings.lowVoltageDisconnect, "lowVoltageDisconnect", settings.lowVoltageDisconnect)}
          {numberPair(
            t.tracer.settings.dischargingLimitVoltage,
            "dischargingLimitVoltage",
            settings.dischargingLimitVoltage
          )}
          {numberPair(t.tracer.settings.eqChargingCycleDays, "eqChargingCycleDays", settings.eqChargingCycleDays, 1)}
          {numberPair(t.tracer.settings.batTempWarnHigh, "batTempWarnHigh", settings.batTempWarnHigh)}
          {numberPair(t.tracer.settings.batTempWarnLow, "batTempWarnLow", settings.batTempWarnLow)}
        </div>

        <Button onClick={handleSave} disabled={busy || Object.keys(dirty).length === 0}>
          {busy ? t.app.loading : t.tracer.settings.saveButton}
        </Button>
      </CardContent>
    </Card>
  );
};
